import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { colors, fonts, radii, shadows, zIndex } from './tokens';
import { Button } from './Button';
import { CheckCircleIcon, InfoIcon, AlertIcon, CloseIcon } from './icons';

// ---------------------------------------------------------------------------------------------
// ConfirmDialog - Figma "Dialog/Leave": light-teal box, radius 8, padding 24, gap 32.
// [No] is rose, [Yes] is teal. "Are you sure you want to save the test results?"
// ---------------------------------------------------------------------------------------------

const fade = keyframes`from{opacity:0}to{opacity:1}`;
const pop = keyframes`from{opacity:0;transform:translateY(8px) scale(0.98)}to{opacity:1;transform:none}`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.dialog};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: ${colors.scrim};
  animation: ${fade} 120ms ease-out;
`;

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: min(380px, 100%);
  padding: 24px;
  box-sizing: border-box;
  background: ${colors.lightTeal};
  border-radius: ${radii.dialog}px;
  box-shadow: ${shadows.dialog};
  font-family: ${fonts.family};
  animation: ${pop} 140ms ease-out;
`;

const Message = styled.p`
  margin: 0;
  font-size: 17px;
  line-height: 1.4;
  font-weight: 700;
  color: ${colors.slate[600]};
`;

const Detail = styled.p`
  margin: -20px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${colors.textMuted};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

/**
 * Controlled confirm dialog.
 * @param open, message, detail, yesLabel ('Yes'), noLabel ('No'), onYes, onNo, busy
 * @param tone 'default' | 'danger' (danger: Yes is red, No is secondary - for destructive actions)
 */
export const ConfirmDialog = ({ open, message, detail, yesLabel = 'Yes', noLabel = 'No', onYes, onNo, busy = false, tone = 'default' }) => {
  const yesRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    yesRef.current?.focus();
    const onKey = (e) => e.key === 'Escape' && onNo?.();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onNo]);
  if (!open) return null;
  return createPortal(
    <Backdrop onClick={(e) => e.target === e.currentTarget && !busy && onNo?.()}>
      <Box role='alertdialog' aria-modal='true' aria-labelledby='confirm-dialog-message'>
        <Message id='confirm-dialog-message'>{message}</Message>
        {detail && <Detail>{detail}</Detail>}
        <Actions>
          <Button variant={tone === 'danger' ? 'secondary' : 'destructive'} onClick={onNo} disabled={busy} style={{ minWidth: 64 }}>
            {noLabel}
          </Button>
          <Button ref={yesRef} variant={tone === 'danger' ? 'destructive' : 'primary'} onClick={onYes} disabled={busy} style={{ minWidth: 64 }}>
            {busy ? 'Please wait…' : yesLabel}
          </Button>
        </Actions>
      </Box>
    </Backdrop>,
    document.body,
  );
};

// ---------------------------------------------------------------------------------------------
// Toasts - Figma toast: white, coloured left border, icon, title, body, optional link, close x.
// ---------------------------------------------------------------------------------------------

const slide = keyframes`from{opacity:0;transform:translateY(-12px)}to{opacity:1;transform:none}`;

const ToastStack = styled.div`
  position: fixed;
  z-index: ${zIndex.toast};
  right: 16px;
  /* Below the session header, so toasts never cover the Back/Next nav row. */
  top: calc(env(safe-area-inset-top, 0px) + 72px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: min(420px, calc(100vw - 32px));
  pointer-events: none;
`;

const TONES = {
  success: { color: colors.success[500], Icon: CheckCircleIcon },
  info: { color: '#3B82F6', Icon: InfoIcon },
  warning: { color: colors.warning[500], Icon: AlertIcon },
  error: { color: colors.error[500], Icon: AlertIcon },
};

const ToastBox = styled.div`
  pointer-events: auto;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 14px 14px 16px;
  background: #fff;
  border-left: 4px solid ${({ tone }) => TONES[tone].color};
  border-radius: 6px;
  box-shadow: ${shadows.toast};
  font-family: ${fonts.family};
  animation: ${slide} 160ms ease-out;
  > svg {
    color: ${({ tone }) => TONES[tone].color};
    margin-top: 1px;
  }
  strong {
    display: block;
    font-size: 14px;
    color: ${colors.slate[800]};
  }
  p {
    margin: 2px 0 0;
    font-size: 13px;
    line-height: 1.45;
    color: ${colors.textMuted};
  }
  button.toast-close {
    margin-left: auto;
    border: 0;
    background: transparent;
    color: ${colors.slate[400]};
    cursor: pointer;
    padding: 2px;
  }
  button.toast-action {
    margin-top: 6px;
    border: 0;
    padding: 0;
    background: transparent;
    color: ${colors.accent};
    font: inherit;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    text-decoration: underline;
  }
`;

/** Presentational toast (used by ToastProvider and the Showcase). */
export const Toast = ({ tone = 'info', title, body, actionLabel, onAction, onClose }) => {
  const { Icon } = TONES[tone] || TONES.info;
  return (
    <ToastBox tone={TONES[tone] ? tone : 'info'} role={tone === 'error' ? 'alert' : 'status'}>
      <Icon size={20} />
      <div>
        {title && <strong>{title}</strong>}
        {body && <p>{body}</p>}
        {actionLabel && (
          <button type='button' className='toast-action' onClick={onAction}>
            {actionLabel}
          </button>
        )}
      </div>
      {onClose && (
        <button type='button' className='toast-close' aria-label='Dismiss' onClick={onClose}>
          <CloseIcon size={16} />
        </button>
      )}
    </ToastBox>
  );
};

// ---------------------------------------------------------------------------------------------
// SessionUIProvider: promise-based confirm() and toast() for any screen.
//   const { confirm, toast } = useSessionUI();
//   if (await confirm({ message: 'Are you sure you want to save the test results?' })) save();
//   toast({ tone: 'success', title: 'Results saved' });
// Without the provider, confirm falls back to window.confirm and toast to console.
// ---------------------------------------------------------------------------------------------

const UIContext = createContext(null);
let toastSeq = 0;

export const SessionUIProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const [toasts, setToasts] = useState([]);

  const confirm = useCallback(
    (opts) =>
      new Promise((resolve) => {
        const o = typeof opts === 'string' ? { message: opts } : opts;
        setDialog({ ...o, resolve });
      }),
    [],
  );

  const dismiss = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const toast = useCallback(
    (opts) => {
      const id = ++toastSeq;
      const t = typeof opts === 'string' ? { title: opts } : opts;
      setToasts((list) => [...list.slice(-2), { ...t, id }]);
      const ttl = t.duration ?? (t.tone === 'error' ? 8000 : 4000);
      if (ttl > 0) setTimeout(() => dismiss(id), ttl);
      return id;
    },
    [dismiss],
  );

  const close = (answer) => {
    dialog?.resolve(answer);
    setDialog(null);
  };

  return (
    <UIContext.Provider value={{ confirm, toast, dismiss }}>
      {children}
      <ConfirmDialog open={Boolean(dialog)} {...(dialog || {})} onYes={() => close(true)} onNo={() => close(false)} />
      {toasts.length > 0 &&
        createPortal(
          <ToastStack>
            {toasts.map((t) => (
              <Toast key={t.id} {...t} onClose={() => dismiss(t.id)} />
            ))}
          </ToastStack>,
          document.body,
        )}
    </UIContext.Provider>
  );
};

const fallbackUI = {
  confirm: (opts) => Promise.resolve(window.confirm(typeof opts === 'string' ? opts : opts?.message)),
  toast: (opts) => console.info('[toast]', opts),
  dismiss: () => {},
};

export const useSessionUI = () => useContext(UIContext) || fallbackUI;
